import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Policies } from './pages/Policies';
import { PolicyForm } from './pages/PolicyForm';
import { PolicyDetail } from './pages/PolicyDetail';
import { Reminders } from './pages/Reminders';
import { PaymentReminders } from './pages/PaymentReminders';
import { Catalogs } from './pages/Catalogs';
import { Plans } from './pages/Plans';
import { CommissionConfig } from './pages/CommissionConfig';
import { UserManagement } from './pages/UserManagement';
import { PrincipalUserManagement } from './pages/PrincipalUserManagement';
import EmailCampaigns from './pages/EmailCampaigns';
import EmailCampaignsApproval from './pages/EmailCampaignsApproval';
import IndividualMessageReview from './pages/IndividualMessageReview';
import EmailResendConfig from './pages/EmailResendConfig';
import OutboxInbox from './pages/OutboxInbox';
import TemplateConfig from './pages/TemplateConfig';
import EmailSettings from './pages/EmailSettings';
import EmailCampaignPreview from './pages/EmailCampaignPreview';
import EmailCampaignEditor from './pages/EmailCampaignEditor';
import EmailCampaignSend from './pages/EmailCampaignSend';
import EmailHistory from './pages/EmailHistory';
import SystemConfigPage from './pages/SystemConfigPage';

import SystemResendConfig from './pages/SystemResendConfig';
import { EmailLogs } from './pages/EmailLogs';
import { ClientsManagement } from './pages/ClientsManagement';
import { ClientsManagementOptimized } from './pages/ClientsManagementOptimized';
import { ClientProfile } from './pages/ClientProfile';
import { ClientProfileOptimized } from './pages/ClientProfileOptimized';
import { ClientPortalLogin } from './pages/ClientPortalLogin';
import { ClientPortalDashboard } from './pages/ClientPortalDashboard';
import { LeadsPipeline } from './pages/LeadsPipeline';
import LeadsBoardOptimized from './pages/LeadsBoardOptimized';
import { LeadDetail } from './pages/LeadDetail';
import { EmailVerificationPending } from './pages/EmailVerificationPending';
import { VerifyEmail } from './pages/VerifyEmail';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Toaster } from './components/ui/sonner';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-500">Cargando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-500">Cargando...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/policies"
        element={
          <ProtectedRoute>
            <Policies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/policies/new"
        element={
          <ProtectedRoute>
            <PolicyForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/policies/:id"
        element={
          <ProtectedRoute>
            <PolicyDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/policies/:id/edit"
        element={
          <ProtectedRoute>
            <PolicyForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <PaymentReminders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reminders"
        element={
          <ProtectedRoute>
            <Reminders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalogs"
        element={
          <ProtectedRoute>
            <Catalogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans"
        element={
          <ProtectedRoute>
            <Plans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/commission-config"
        element={
          <ProtectedRoute>
            <CommissionConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/principal-users"
        element={
          <ProtectedRoute>
            <PrincipalUserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-campaigns"
        element={
          <ProtectedRoute>
            <EmailCampaigns />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-campaigns/config"
        element={
          <ProtectedRoute>
            <EmailResendConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-campaigns/history"
        element={
          <ProtectedRoute>
            <EmailHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-campaigns/scheduled"
        element={
          <ProtectedRoute>
            <EmailLogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-campaigns/:campaignId/preview"
        element={
          <ProtectedRoute>
            <EmailCampaignPreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-campaigns/:campaignId/edit"
        element={
          <ProtectedRoute>
            <EmailCampaignEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-campaigns/:campaignId/send"
        element={
          <ProtectedRoute>
            <EmailCampaignSend />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-campaigns/config/system"
        element={
          <ProtectedRoute>
            <SystemConfigPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-campaigns/approval"
        element={
          <ProtectedRoute>
            <EmailCampaignsApproval />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-campaigns/messages/review"
        element={
          <ProtectedRoute>
            <IndividualMessageReview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-outbox"
        element={
          <ProtectedRoute>
            <OutboxInbox />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-templates"
        element={
          <ProtectedRoute>
            <TemplateConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-settings"
        element={
          <ProtectedRoute>
            <EmailSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-logs"
        element={
          <ProtectedRoute>
            <EmailLogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-settings"
        element={
          <ProtectedRoute>
            <EmailSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <ClientsManagementOptimized />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:clientId"
        element={
          <ProtectedRoute>
            <ClientProfileOptimized />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pipeline"
        element={
          <ProtectedRoute>
            <LeadsBoardOptimized />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <LeadsBoardOptimized />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/new"
        element={
          <ProtectedRoute>
            <LeadDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:leadId"
        element={
          <ProtectedRoute>
            <LeadDetail />
          </ProtectedRoute>
        }
      />
      
      {/* Auth Routes - Públicas */}
      <Route path="/email-verification-pending" element={<EmailVerificationPending />} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      
      <Route
        path="/system/resend-config"
        element={
          <ProtectedRoute>
            <SystemResendConfig />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <Routes>
        {/* Rutas públicas del Portal de Clientes - FUERA del AuthProvider */}
        <Route path="/portal/:accessToken" element={<ClientPortalLogin />} />
        <Route path="/portal/dashboard" element={<ClientPortalDashboard />} />
        
        {/* Rutas del sistema admin - DENTRO del AuthProvider */}
        <Route path="/*" element={
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
